import { BasicSelectField } from 'lib/iterable-hook-form';
import { SyntheticEvent } from 'react';
import styled from 'styled-components';
import { ErrorBanner } from 'ui/banner';
import { BUTTON_SIZES, Button } from 'ui/button';
import { FormField } from 'ui/formField';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { Body2, colors, spacing } from 'ui/theme';
import { FIELD_TYPE_OPTIONS } from '../../constants';
import { CompoundNameInputField } from './CompoundNameInputField';
import { ControlledInputField } from './ControlledInputField';

const StyledBasicSelect = styled(BasicSelectField)`
  width: 100%;
`;

const StyledMessage = styled.span`
  color: ${colors.textSecondary};
`;

const StyledName = styled.span`
  padding: ${spacing.eighth} ${spacing.quarter};
  font-family: Menlo, Monaco, Consolas, 'Lucida Console', monospace;
  color: ${colors.textPrimary};
  background-color: ${colors.grey10};
  border-radius: ${spacing.eighth};
`;

const StyledErrorBanner = styled(ErrorBanner)`
  margin-bottom: ${spacing.base};
`;

export const CREATE_FIELD_MODAL_TEXT = {
  formId: 'create-field-modal-form',
  genericErrorMessage: 'There was a problem while creating the field. Please try again.',
  modalId: 'create-user-field-modal',
  nameLabelText: 'Name',
  saveButtonText: 'Save',
  title: 'Define new field',
  typeHelpTetxt: 'Field type cannot be changed once the field has been created',
  typeLabelText: 'Type',
  typeSelectId: 'field-type-select',
};

type CreateFieldModalProps = {
  onClose: () => void;
  isDisabled: boolean;
  isSubmitting: boolean;
  handleSubmit: () => void;
  parentName?: string;
  hasError: boolean;
};

export const CreateFieldModal = ({
  handleSubmit,
  hasError,
  isDisabled,
  isSubmitting,
  onClose,
  parentName,
}: CreateFieldModalProps) => {
  const onSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    handleSubmit();
  };
  return (
    <Modal id={CREATE_FIELD_MODAL_TEXT.modalId} withPortal isModalOpen onClose={onClose}>
      <ModalHeader onClose={onClose} autoFocus={false}>
        {CREATE_FIELD_MODAL_TEXT.title}
      </ModalHeader>
      <ModalBody>
        <form
          data-test={CREATE_FIELD_MODAL_TEXT.formId}
          id={CREATE_FIELD_MODAL_TEXT.formId}
          onSubmit={onSubmit}
        >
          {hasError && (
            <StyledErrorBanner>{CREATE_FIELD_MODAL_TEXT.genericErrorMessage}</StyledErrorBanner>
          )}
          {parentName && (
            <Body2>
              <StyledMessage>Adding a new field within &nbsp;</StyledMessage>
              <StyledName>{parentName}</StyledName>
            </Body2>
          )}
          <CompoundNameInputField
            labelText={CREATE_FIELD_MODAL_TEXT.nameLabelText}
            name="fieldName"
          />
          <FormField
            helpText={CREATE_FIELD_MODAL_TEXT.typeHelpTetxt}
            labelText={CREATE_FIELD_MODAL_TEXT.typeLabelText}
          >
            <StyledBasicSelect
              data-test={CREATE_FIELD_MODAL_TEXT.typeSelectId}
              name="fieldType"
              options={FIELD_TYPE_OPTIONS}
            />
          </FormField>
          <ControlledInputField
            formFieldId="field-description"
            inputId="field-description-input"
            labelText="Description"
            name="description"
          />
          <ControlledInputField
            formFieldId="field-sample-value"
            inputId="field-sample-value-input"
            labelText="Sample value"
            name="sampleValue"
          />
        </form>
      </ModalBody>
      <ModalFooter onClose={onClose}>
        <Button
          data-test="save-form-button"
          isDisabled={isDisabled}
          isLoading={isSubmitting}
          onClick={onSubmit}
          size={BUTTON_SIZES.LARGE}
          type="submit"
          form={CREATE_FIELD_MODAL_TEXT.formId}
        >
          {CREATE_FIELD_MODAL_TEXT.saveButtonText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
